(() => {
  if (window.ArthurSkeletonLoader) return;

  const selector = 'img[data-skeleton-lazy][data-src]';
  const loadedSelector = '[data-img-loaded]';
  const preloadMargin = 250;
  const observed = new WeakSet();
  let refreshQueued = false;

  const isCollectionPage = () => window.location.pathname.includes('/collections/');

  const getLargestSrcsetCandidate = (srcset) => {
    if (!srcset) return null;

    return srcset.split(',').reduce((largest, candidate) => {
      const parts = candidate.trim().split(/\s+/);
      const url = parts[0];
      const width = parseInt((parts[1] || '').replace('w', ''), 10);

      if (!url || Number.isNaN(width)) return largest;
      if (!largest || width > largest.width) return { url, width };

      return largest;
    }, null);
  };

  const forceTopQualityImage = (img) => {
    // Disabled 2026-06-16 (incident: mobile PDP/collection OOM crashes).
    // This previously pinned every collection tile to its 3000w master at
    // sizes="100vw", decoding ~43MB of bitmap RAM PER tile -> a 12-24 tile
    // grid exhausted the ~250MB memory ceiling of iOS in-app webviews
    // (Instagram/Mail) and WebKit killed the tab ("Can't open this page").
    // The template's responsive srcset + sizes (product-tile.liquid) already
    // serves correctly-sized images, so we keep those untouched.
    return;
  };

  const forceTopQualityImages = (root = document) => {
    if (!isCollectionPage()) return;

    const images = root.matches?.('.product-tile img[srcset]')
      ? [root]
      : Array.from(root.querySelectorAll?.('.product-tile img[srcset]') || []);

    images.forEach(forceTopQualityImage);
  };

  const markLoaded = (img) => {
    const wrapper = img.closest(loadedSelector);
    if (wrapper) {
      wrapper.dataset.imgLoaded = 'true';
    }
  };

  const reveal = (img) => {
    if (!img || img.dataset.skeletonRevealQueued === 'true') return;

    img.dataset.skeletonRevealQueued = 'true';

    const finish = () => {
      requestAnimationFrame(() => {
        img.dataset.skeletonRevealed = 'true';
        markLoaded(img);
      });
    };

    if (img.decode && img.complete && img.naturalWidth > 0) {
      img.decode().then(finish).catch(finish);
    } else {
      finish();
    }
  };

  const hydrate = (img) => {
    if (!img || img.dataset.skeletonHydrated === 'true') return;

    img.dataset.skeletonHydrated = 'true';

    if (img.dataset.fetchpriority) {
      img.setAttribute('fetchpriority', img.dataset.fetchpriority);
    }

    if (img.dataset.sizes) {
      img.sizes = img.dataset.sizes;
    }

    if (img.dataset.srcset) {
      img.srcset = img.dataset.srcset;
    }

    img.src = img.dataset.src;
    forceTopQualityImage(img);

    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
    img.removeAttribute('data-sizes');

    if (img.complete && img.currentSrc) {
      reveal(img);
    }
  };

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || !isNearViewport(entry.target)) return;
          observer.unobserve(entry.target);
          hydrate(entry.target);
        });
      }, { rootMargin: '250px 0px' })
    : null;

  const init = (root = document) => {
    const images = root.matches?.(selector)
      ? [root]
      : Array.from(root.querySelectorAll?.(selector) || []);

    images.forEach((img) => {
      if (observed.has(img)) return;
      observed.add(img);

      if (observer) {
        observer.observe(img);
      } else {
        hydrate(img);
      }
    });
  };

  const isNearViewport = (img) => {
    const rect = img.getBoundingClientRect();

    if (rect.width === 0 || rect.height === 0) return false;

    return rect.bottom >= -preloadMargin &&
      rect.right >= -preloadMargin &&
      rect.top <= window.innerHeight + preloadMargin &&
      rect.left <= window.innerWidth + preloadMargin;
  };

  const hydrateNearViewport = (root = document) => {
    const images = root.matches?.(selector)
      ? [root]
      : Array.from(root.querySelectorAll?.(selector) || []);

    images.forEach((img) => {
      if (isNearViewport(img)) {
        hydrate(img);
      }
    });
  };

  const refresh = () => {
    init();
    hydrateNearViewport();
    forceTopQualityImages();
  };

  const queueRefresh = () => {
    if (refreshQueued) return;
    refreshQueued = true;

    requestAnimationFrame(() => {
      refreshQueued = false;
      refresh();
    });
  };

  document.addEventListener('load', (event) => {
    if (event.target?.matches?.(`${selector}, img[data-skeleton-hydrated="true"]`)) {
      reveal(event.target);
    }
  }, true);

  document.addEventListener('error', (event) => {
    if (event.target?.matches?.(`${selector}, img[data-skeleton-hydrated="true"]`)) {
      markLoaded(event.target);
    }
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', queueRefresh);
  } else {
    queueRefresh();
  }

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          init(node);
          hydrateNearViewport(node);
          forceTopQualityImages(node);
        }
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.addEventListener('load', queueRefresh, { once: true });
  window.addEventListener('resize', queueRefresh, { passive: true });
  window.addEventListener('scroll', queueRefresh, { passive: true });
  document.addEventListener('shopify:section:load', queueRefresh);

  [100, 500, 1500, 3000].forEach((delay) => {
    window.setTimeout(queueRefresh, delay);
  });

  window.ArthurSkeletonLoader = { hydrate, init, markLoaded, refresh, reveal };
})();
